import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import { createElement } from "@wordpress/element";

/**
 * External dependencies
 */
import { noop, omit } from 'lodash';
import classnames from 'classnames';
/**
 * WordPress dependencies
 */

import { forwardRef, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { ENTER } from '@wordpress/keycodes';
/**
 * Internal dependencies
 */

import { inputControlActionTypes, composeStateReducers } from '../input-control/state';
import { Root, ValueInput } from './styles/unit-control-styles';
import UnitSelectControl from './unit-select-control';
import { CSS_UNITS, getParsedValue, getValidParsedUnit } from './utils';
import { useControlledState } from '../utils/hooks';

function UnitControl(_ref, ref) {
  var _ref$__unstableStateR = _ref.__unstableStateReducer,
      stateReducer = _ref$__unstableStateR === void 0 ? function (state) {
    return state;
  } : _ref$__unstableStateR,
      _ref$autoComplete = _ref.autoComplete,
      autoComplete = _ref$autoComplete === void 0 ? 'off' : _ref$autoComplete,
      className = _ref.className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$disableUnits = _ref.disableUnits,
      disableUnits = _ref$disableUnits === void 0 ? false : _ref$disableUnits,
      _ref$isPressEnterToCh = _ref.isPressEnterToChange,
      isPressEnterToChange = _ref$isPressEnterToCh === void 0 ? false : _ref$isPressEnterToCh,
      _ref$isResetValueOnUn = _ref.isResetValueOnUnitChange,
      isResetValueOnUnitChange = _ref$isResetValueOnUn === void 0 ? false : _ref$isResetValueOnUn,
      _ref$isUnitSelectTabb = _ref.isUnitSelectTabbable,
      isUnitSelectTabbable = _ref$isUnitSelectTabb === void 0 ? true : _ref$isUnitSelectTabb,
      label = _ref.label,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
      _ref$onUnitChange = _ref.onUnitChange,
      onUnitChange = _ref$onUnitChange === void 0 ? noop : _ref$onUnitChange,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'default' : _ref$size,
      style = _ref.style,
      unitProp = _ref.unit,
      _ref$units = _ref.units,
      units = _ref$units === void 0 ? CSS_UNITS : _ref$units,
      valueProp = _ref.value,
      props = _objectWithoutProperties(_ref, ["__unstableStateReducer", "autoComplete", "className", "disabled", "disableUnits", "isPressEnterToChange", "isResetValueOnUnitChange", "isUnitSelectTabbable", "label", "onChange", "onUnitChange", "size", "style", "unit", "units", "value"]);

  var _getParsedValue = getParsedValue(valueProp, unitProp, units),
      _getParsedValue2 = _slicedToArray(_getParsedValue, 2),
      value = _getParsedValue2[0],
      initialUnit = _getParsedValue2[1];

  var _useControlledState = useControlledState(unitProp, {
    initial: initialUnit
  }),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      unit = _useControlledState2[0],
      setUnit = _useControlledState2[1]; // Stores parsed value for hand-off in state reducer


  var refParsedValue = useRef(null);
  var classes = classnames('components-unit-control', className);

  var handleOnChange = function handleOnChange(next, changeProps) {
    if (next === '') {
      onChange('', changeProps);
      return;
    }
    /*
     * Customizing the onChange callback.
     * This allows as to broadcast a combined value+unit to onChange.
     */


    next = getValidParsedUnit(next, units, value, unit).join('');
    onChange(next, changeProps);
  };

  var handleOnUnitChange = function handleOnUnitChange(next, changeProps) {
    var data = changeProps.data;
    var nextValue = "".concat(value).concat(next);

    if (isResetValueOnUnitChange && (data === null || data === void 0 ? void 0 : data.default) !== undefined) {
      nextValue = "".concat(data.default).concat(next);
    }

    onChange(nextValue, changeProps);
    onUnitChange(next, changeProps);
    setUnit(next);
  };

  var mayUpdateUnit = function mayUpdateUnit(event) {
    if (!isNaN(event.target.value)) {
      refParsedValue.current = null;
      return;
    }

    var _getValidParsedUnit = getValidParsedUnit(event.target.value, units, value, unit),
        _getValidParsedUnit2 = _slicedToArray(_getValidParsedUnit, 2),
        parsedValue = _getValidParsedUnit2[0],
        parsedUnit = _getValidParsedUnit2[1];

    refParsedValue.current = parsedValue;

    if (isPressEnterToChange && parsedUnit !== unit) {
      var data = units.find(function (option) {
        return option.value === parsedUnit;
      });
      var changeProps = {
        event: event,
        data: data
      };
      onChange("".concat(parsedValue).concat(parsedUnit), changeProps);
      onUnitChange(parsedUnit, changeProps);
      setUnit(parsedUnit);
    }
  };

  var handleOnBlur = mayUpdateUnit;

  var handleOnKeyDown = function handleOnKeyDown(event) {
    var keyCode = event.keyCode;

    if (keyCode === ENTER) {
      mayUpdateUnit(event);
    }
  };
  /**
   * "Middleware" function that intercepts updates from InputControl.
   * This allows us to tap into actions to transform the (next) state for
   * InputControl.
   *
   * @param {Object} state State from InputControl
   * @param {Object} action Action triggering state change
   * @return {Object} The updated state to apply to InputControl
   */


  var unitControlStateReducer = function unitControlStateReducer(state, action) {
    /*
     * On commits (when pressing ENTER and on blur if
     * isPressEnterToChange is true), if a parse has been performed
     * then use that result to update the state.
     */
    if (action.type === inputControlActionTypes.COMMIT) {
      if (refParsedValue.current !== null) {
        state.value = refParsedValue.current;
        refParsedValue.current = null;
      }
    }

    return state;
  };

  var inputSuffix = !disableUnits ? createElement(UnitSelectControl, {
    "aria-label": __('Select unit'),
    disabled: disabled,
    isTabbable: isUnitSelectTabbable,
    options: units,
    onChange: handleOnUnitChange,
    size: size,
    value: unit
  }) : null;
  return createElement(Root, {
    className: "components-unit-control-wrapper",
    style: style
  }, createElement(ValueInput, _extends({
    "aria-label": label,
    type: isPressEnterToChange ? 'text' : 'number'
  }, omit(props, ['children']), {
    autoComplete: autoComplete,
    className: classes,
    disabled: disabled,
    disableUnits: disableUnits,
    isPressEnterToChange: isPressEnterToChange,
    label: label,
    onBlur: handleOnBlur,
    onKeyDown: handleOnKeyDown,
    onChange: handleOnChange,
    ref: ref,
    size: size,
    suffix: inputSuffix,
    value: value,
    __unstableStateReducer: composeStateReducers(unitControlStateReducer, stateReducer)
  })));
}

var ForwardedUnitControl = forwardRef(UnitControl);
export default ForwardedUnitControl;
//# sourceMappingURL=index.js.map